import "./Roi_new.css";
import { useEffect, useMemo, useState } from "react";

export default function Roi_new() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [multiData, setMultiData] = useState({});
  const [singleData, setSingleData] = useState([]);

  const [multiFilter, setMultiFilter] = useState("all");
  const [singleFilter, setSingleFilter] = useState("all");
  const API_URL = process.env.REACT_APP_API_FAST || "http://192.168.1.103:8000";

  const fetchRoiData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/roi/get-data`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      console.log("ROI DATA:", data);

      setMultiData(data?.multi_bet_return || {});
      setSingleData(data?.single_bet_return || []);
    } catch (err) {
      console.error("ROI fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoiData();
  }, []);

  /* =========================================================
     ARRAYS
  ========================================================= */

  const multiBets = useMemo(() => {
    return Object.values(multiData || {});
  }, [multiData]);

  const individualBets = useMemo(() => {
    return Array.isArray(singleData) ? singleData : [];
  }, [singleData]);

  /* =========================================================
     FILTERED DATA
  ========================================================= */

  const filteredMultiBets = useMemo(() => {
    if (multiFilter === "all") {
      return multiBets;
    }

    return multiBets.filter((bet) => bet.status?.toLowerCase() === multiFilter);
  }, [multiBets, multiFilter]);

  const filteredIndividualBets = useMemo(() => {
    if (singleFilter === "all") {
      return individualBets;
    }

    return individualBets.filter(
      (bet) => bet.status?.toLowerCase() === singleFilter
    );
  }, [individualBets, singleFilter]);

  /* =========================================================
     MULTI TOTALS
  ========================================================= */

  const multiTotals = useMemo(() => {
    const totalStake = multiBets.reduce(
      (total, bet) => total + Number(bet.stake || 0),
      0
    );

    const totalReturns = multiBets.reduce(
      (total, bet) => total + Number(bet.returns || 0),
      0
    );

    const totalProfitLoss = multiBets.reduce(
      (total, bet) => total + Number(bet.profit_loss || 0),
      0
    );

    const roi = totalStake > 0 ? (totalProfitLoss / totalStake) * 100 : 0;

    const wins = multiBets.filter(
      (bet) => bet.status?.toLowerCase() === "won"
    ).length;

    const losses = multiBets.filter(
      (bet) => bet.status?.toLowerCase() === "lost"
    ).length;

    return {
      totalStake,
      totalReturns,
      totalProfitLoss,
      roi,
      wins,
      losses,
      totalBets: multiBets.length,
    };
  }, [multiBets]);

  /* =========================================================
     INDIVIDUAL TOTALS
  ========================================================= */

  const individualTotals = useMemo(() => {
    const totalStake = individualBets.reduce(
      (total, bet) => total + Number(bet.stake || 0),
      0
    );

    const totalReturns = individualBets.reduce(
      (total, bet) => total + Number(bet.return || 0),
      0
    );

    const totalProfitLoss = individualBets.reduce(
      (total, bet) => total + Number(bet.profit_loss_single || 0),
      0
    );

    const roi = totalStake > 0 ? (totalProfitLoss / totalStake) * 100 : 0;

    const wins = individualBets.filter(
      (bet) => bet.status?.toLowerCase() === "won"
    ).length;

    const losses = individualBets.filter(
      (bet) => bet.status?.toLowerCase() === "lost"
    ).length;

    return {
      totalStake,
      totalReturns,
      totalProfitLoss,
      roi,
      wins,
      losses,
      totalBets: individualBets.length,
    };
  }, [individualBets]);

  /* =========================================================
     OVERALL TOTALS
  ========================================================= */

  const overallTotals = useMemo(() => {
    const totalBets = multiTotals.totalBets + individualTotals.totalBets;

    const totalStake = multiTotals.totalStake + individualTotals.totalStake;

    const totalReturns =
      multiTotals.totalReturns + individualTotals.totalReturns;

    const totalProfitLoss =
      multiTotals.totalProfitLoss + individualTotals.totalProfitLoss;

    const wins = multiTotals.wins + individualTotals.wins;

    const losses = multiTotals.losses + individualTotals.losses;

    const roi = totalStake > 0 ? (totalProfitLoss / totalStake) * 100 : 0;

    return {
      totalBets,
      totalStake,
      totalReturns,
      totalProfitLoss,
      wins,
      losses,
      roi,
    };
  }, [multiTotals, individualTotals]);

  /* =========================================================
     FORMATTERS
  ========================================================= */

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString("en-KE", {
      maximumFractionDigits: 2,
    });
  };

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("en-KE", {
      maximumFractionDigits: 2,
    });
  };

  const getProfitClass = (value) => {
    if (Number(value) > 0) return "roi_profit";
    if (Number(value) < 0) return "roi_loss";
    return "roi_neutral";
  };

  const getStatusClass = (status) => {
    return status?.toLowerCase() === "won"
      ? "roi_status roi_win_status"
      : "roi_status roi_loss_status";
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="alg1_card roi_main_new">
        <header className="roi_header">
          <div className="roi_header_title">
            <div className="roi_skeleton roi_skeleton_title"></div>
            <div className="roi_skeleton roi_skeleton_subtitle"></div>
          </div>

          <div className="roi_skeleton roi_skeleton_button"></div>
        </header>

        <div className="roi_body">
          <div className="roi_left">
            <div className="roi_skeleton_panel"></div>
            <div className="roi_skeleton_panel"></div>
          </div>

          <aside className="roi_right">
            <div className="roi_skeleton_panel"></div>
            <div className="roi_skeleton_panel"></div>
          </aside>
        </div>
      </div>
    );
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error) {
    return (
      <div className="alg1_card roi_main_new">
        <div className="roi_error">
          <strong>Unable to load ROI data</strong>
          <span>{error}</span>

          <button className="roi_refresh_button" onClick={fetchRoiData}>
            ↻ Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="alg1_card roi_main_new">
      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="roi_header">
        <div className="roi_header_title">
          <span className="roi_eyebrow">BETTING ANALYTICS</span>

          <h1>ROI ANALYTICS</h1>

          <p>Monitor returns, profitability and betting performance</p>
        </div>

        <div className="roi_header_actions">
          <button className="roi_refresh_button" onClick={fetchRoiData}>
            ↻ Refresh
          </button>
        </div>
      </header>

      {/* =====================================================
          BODY
      ===================================================== */}

      <main className="roi_body">
        {/* ===================================================
            LEFT
        =================================================== */}

        <section className="roi_left">
          {/* =================================================
              MULTI BET
          ================================================= */}

          <section className="roi_panel">
            <div className="roi_panel_header">
              <div>
                <h2>Multi-Bet Performance</h2>

                <span>Performance by betting instance</span>
              </div>

              <div className="roi_header_filter">
                <button
                  className={
                    multiFilter === "all" ? "roi_filter active" : "roi_filter"
                  }
                  onClick={() => setMultiFilter("all")}
                >
                  ALL
                </button>

                <button
                  className={
                    multiFilter === "won"
                      ? "roi_filter active roi_filter_win"
                      : "roi_filter"
                  }
                  onClick={() => setMultiFilter("won")}
                >
                  WIN
                </button>

                <button
                  className={
                    multiFilter === "lost"
                      ? "roi_filter active roi_filter_loss"
                      : "roi_filter"
                  }
                  onClick={() => setMultiFilter("lost")}
                >
                  LOSS
                </button>

                <span className="roi_panel_badge">{multiTotals.totalBets}</span>
              </div>
            </div>

            <div className="roi_table_container">
              <table className="roi_table">
                <thead>
                  <tr>
                    <th>Instance</th>
                    <th>Total Odds</th>
                    <th>Stake</th>
                    <th>Status</th>
                    <th>Return</th>
                    <th>Profit / Loss</th>
                    <th>ROI</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredMultiBets.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="roi_empty">
                        No multi-bets found
                      </td>
                    </tr>
                  ) : (
                    filteredMultiBets.map((bet) => {
                      const roi =
                        Number(bet.stake) > 0
                          ? (Number(bet.profit_loss) / Number(bet.stake)) * 100
                          : 0;

                      return (
                        <tr key={bet.instance_id}>
                          <td>#{bet.instance_id}</td>

                          <td>{formatNumber(bet.total_odds)}</td>

                          <td>KSh {formatMoney(bet.stake)}</td>

                          <td>
                            <span className={getStatusClass(bet.status)}>
                              {bet.status?.toUpperCase()}
                            </span>
                          </td>

                          <td>KSh {formatMoney(bet.returns)}</td>

                          <td className={getProfitClass(bet.profit_loss)}>
                            {Number(bet.profit_loss) >= 0 ? "+" : "-"}
                            KSh {formatMoney(Math.abs(Number(bet.profit_loss)))}
                          </td>

                          <td className={getProfitClass(roi)}>
                            {roi >= 0 ? "+" : ""}
                            {formatNumber(roi)}%
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ================================
                MULTI TOTALS
            ================================= */}

            <div className="roi_section_totals">
              <div>
                <span>Stake</span>
                <strong>KSh {formatMoney(multiTotals.totalStake)}</strong>
              </div>

              <div>
                <span>Returns</span>
                <strong>KSh {formatMoney(multiTotals.totalReturns)}</strong>
              </div>

              <div>
                <span>Net P/L</span>
                <strong className={getProfitClass(multiTotals.totalProfitLoss)}>
                  {multiTotals.totalProfitLoss >= 0 ? "+" : "-"}
                  KSh {formatMoney(Math.abs(multiTotals.totalProfitLoss))}
                </strong>
              </div>

              <div>
                <span>ROI</span>
                <strong className={getProfitClass(multiTotals.roi)}>
                  {multiTotals.roi >= 0 ? "+" : ""}
                  {formatNumber(multiTotals.roi)}%
                </strong>
              </div>
            </div>
          </section>

          {/* =================================================
              INDIVIDUAL BETS
          ================================================= */}

          <section className="roi_panel">
            <div className="roi_panel_header">
              <div>
                <h2>Individual Bet Performance</h2>

                <span>Individual stake and prediction results</span>
              </div>

              <div className="roi_header_filter">
                <button
                  className={
                    singleFilter === "all" ? "roi_filter active" : "roi_filter"
                  }
                  onClick={() => setSingleFilter("all")}
                >
                  ALL
                </button>

                <button
                  className={
                    singleFilter === "won"
                      ? "roi_filter active roi_filter_win"
                      : "roi_filter"
                  }
                  onClick={() => setSingleFilter("won")}
                >
                  WIN
                </button>

                <button
                  className={
                    singleFilter === "lost"
                      ? "roi_filter active roi_filter_loss"
                      : "roi_filter"
                  }
                  onClick={() => setSingleFilter("lost")}
                >
                  LOSS
                </button>

                <span className="roi_panel_badge">
                  {individualTotals.totalBets}
                </span>
              </div>
            </div>

            <div className="roi_table_container">
              <table className="roi_table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Instance</th>
                    <th>Match</th>
                    <th>Prediction</th>
                    <th>Odd</th>
                    <th>Stake</th>
                    <th>Result</th>
                    <th>Return</th>
                    <th>P/L</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredIndividualBets.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="roi_empty">
                        No individual bets found
                      </td>
                    </tr>
                  ) : (
                    filteredIndividualBets.map((bet, index) => (
                      <tr key={`${bet.instance_id}-${index}`}>
                        <td>#{String(index + 1).padStart(3, "0")}</td>

                        <td>{bet.instance_id}</td>

                        <td>
                          {bet.Home_Team} vs {bet.Away_Team}
                        </td>

                        <td>{bet.Prediction}</td>

                        <td>{Number(bet.odd).toFixed(2)}</td>

                        <td>KSh {formatMoney(bet.stake)}</td>

                        <td>
                          <span className={getStatusClass(bet.status)}>
                            {bet.status?.toUpperCase()}
                          </span>
                        </td>

                        <td>KSh {formatMoney(bet.return)}</td>

                        <td className={getProfitClass(bet.profit_loss_single)}>
                          {Number(bet.profit_loss_single) >= 0 ? "+" : "-"}
                          KSh{" "}
                          {formatMoney(
                            Math.abs(Number(bet.profit_loss_single))
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ================================
                INDIVIDUAL TOTALS
            ================================= */}

            <div className="roi_section_totals">
              <div>
                <span>Stake</span>
                <strong>KSh {formatMoney(individualTotals.totalStake)}</strong>
              </div>

              <div>
                <span>Returns</span>
                <strong>
                  KSh {formatMoney(individualTotals.totalReturns)}
                </strong>
              </div>

              <div>
                <span>Net P/L</span>
                <strong
                  className={getProfitClass(individualTotals.totalProfitLoss)}
                >
                  {individualTotals.totalProfitLoss >= 0 ? "+" : "-"}
                  KSh {formatMoney(Math.abs(individualTotals.totalProfitLoss))}
                </strong>
              </div>

              <div>
                <span>ROI</span>
                <strong className={getProfitClass(individualTotals.roi)}>
                  {individualTotals.roi >= 0 ? "+" : ""}
                  {formatNumber(individualTotals.roi)}%
                </strong>
              </div>
            </div>
          </section>
        </section>

        {/* ===================================================
            RIGHT NAV / SUMMARY
        =================================================== */}

        <aside className="roi_right">
          {/* =================================================
              INDIVIDUAL / MULTI QUICK SUMMARY
          ================================================= */}

          <section className="roi_panel">
            <div className="roi_panel_header">
              <div>
                <h2>Bet Types</h2>
                <span>Performance comparison</span>
              </div>
            </div>
            <div className="roi_type_card">
              <div className="roi_type_card_header">
                <span>INDIVIDUAL</span>

                <strong>{individualTotals.totalBets}</strong>
              </div>

              <div className="roi_type_card_data">
                <div>
                  <span>Stake</span>
                  <strong>
                    KSh {formatMoney(individualTotals.totalStake)}
                  </strong>
                </div>

                <div>
                  <span>P/L</span>
                  <strong
                    className={getProfitClass(individualTotals.totalProfitLoss)}
                  >
                    {individualTotals.totalProfitLoss >= 0 ? "+" : "-"}
                    KSh{" "}
                    {formatMoney(Math.abs(individualTotals.totalProfitLoss))}
                  </strong>
                </div>
              </div>
            </div>
            <div className="roi_type_card">
              <div className="roi_type_card_header">
                <span>MULTI</span>

                <strong>{multiTotals.totalBets}</strong>
              </div>
              <div className="roi_type_card_data">
                <div>
                  <span>won</span>
                  <strong>{multiTotals.wins}</strong>
                </div>

                <div>
                  <span>Loss</span>
                  <strong> {multiTotals.losses}</strong>
                </div>
              </div>

              <div className="roi_type_card_data">
                <div>
                  <span>Stake</span>
                  <strong>KSh {formatMoney(multiTotals.totalStake)}</strong>
                </div>

                <div>
                  <span>P/L</span>
                  <strong
                    className={getProfitClass(multiTotals.totalProfitLoss)}
                  >
                    {multiTotals.totalProfitLoss >= 0 ? "+" : "-"}
                    KSh {formatMoney(Math.abs(multiTotals.totalProfitLoss))}
                  </strong>
                </div>
              </div>
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
}
